import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient, { getErrorMessage } from '../../api/client';
import { useToast } from '../../hooks/useToast';
import { FiPlus, FiUserCheck, FiTrash2 } from 'react-icons/fi';

const TABS = ['Vacancies', 'Candidates', 'Applications', 'Interviews', 'Offers'];

const Recruitment = () => {
  const { success, error: showError } = useToast();
  const [tab, setTab] = useState('Vacancies');

  const [vacancies, setVacancies] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [applications, setApplications] = useState([]);
  const [selectedApplicationId, setSelectedApplicationId] = useState('');
  const [interviews, setInterviews] = useState([]);

  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const [vacancyForm, setVacancyForm] = useState({ title: '', description: '', location: '' });
  const [candidateForm, setCandidateForm] = useState({ firstName: '', lastName: '', email: '', phone: '', source: '' });
  const [applicationForm, setApplicationForm] = useState({ candidateId: '', jobVacancyId: '' });
  const [interviewForm, setInterviewForm] = useState({ candidateApplicationId: '', scheduledAt: '', location: '', meetingLink: '' });
  const [offerForm, setOfferForm] = useState({ candidateApplicationId: '', offeredSalary: '', proposedStartDate: '' });
  const [hireForm, setHireForm] = useState({ applicationId: '', employeeNumber: '', startDate: '', salaryAmount: '' });
  const [showHireFor, setShowHireFor] = useState(null);

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/recruitment/vacancies'),
      apiClient.get('/recruitment/candidates'),
      apiClient.get('/recruitment/applications'),
    ]).then(([vacRes, candRes, appRes]) => {
      setVacancies(vacRes.data.data);
      setCandidates(candRes.data.data);
      setApplications(appRes.data.data);
    }).catch((err) => showError(getErrorMessage(err, 'Could not load recruitment data')))
      .finally(() => setLoading(false));
  };

  useEffect(loadAll, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab === 'Interviews' && selectedApplicationId) {
      apiClient.get(`/recruitment/applications/${selectedApplicationId}/interviews`)
        .then(({ data }) => setInterviews(data.data))
        .catch((err) => showError(getErrorMessage(err, 'Could not load interviews')));
    }
  }, [tab, selectedApplicationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const candidateName = (id) => {
    const c = candidates.find((x) => x.id === id);
    return c ? `${c.firstName} ${c.lastName}` : id;
  };
  const vacancyTitle = (id) => vacancies.find((v) => v.id === id)?.title || id;

  const handleAddVacancy = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/recruitment/vacancies', vacancyForm);
      success('✅ Vacancy created');
      setShowForm(false);
      setVacancyForm({ title: '', description: '', location: '' });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not create vacancy')); }
  };

  const handleVacancyStatus = async (vacancyId, status) => {
    try {
      await apiClient.patch(`/recruitment/vacancies/${vacancyId}`, { status });
      success('✅ Vacancy updated');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not update vacancy')); }
  };

  const handleDeleteVacancy = async (vacancyId) => {
    if (!window.confirm('Delete this vacancy?')) return;
    try {
      await apiClient.delete(`/recruitment/vacancies/${vacancyId}`);
      success('✅ Vacancy deleted');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not delete vacancy')); }
  };

  const handleAddCandidate = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/recruitment/candidates', candidateForm);
      success('✅ Candidate added');
      setShowForm(false);
      setCandidateForm({ firstName: '', lastName: '', email: '', phone: '', source: '' });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not add candidate')); }
  };

  const handleDeleteCandidate = async (candidateId) => {
    if (!window.confirm('Delete this candidate?')) return;
    try {
      await apiClient.delete(`/recruitment/candidates/${candidateId}`);
      success('✅ Candidate deleted');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not delete candidate')); }
  };

  const handleAddApplication = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/recruitment/applications', applicationForm);
      success('✅ Application created');
      setShowForm(false);
      setApplicationForm({ candidateId: '', jobVacancyId: '' });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not create application')); }
  };

  const handleStageChange = async (applicationId, stage) => {
    try {
      await apiClient.patch(`/recruitment/applications/${applicationId}/stage`, { stage });
      success('✅ Stage updated');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not update stage')); }
  };

  const handleDeleteApplication = async (applicationId) => {
    if (!window.confirm('Delete this application?')) return;
    try {
      await apiClient.delete(`/recruitment/applications/${applicationId}`);
      success('✅ Application deleted');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not delete application')); }
  };

  const handleHire = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post(`/recruitment/applications/${showHireFor}/hire`, {
        employeeNumber: hireForm.employeeNumber,
        startDate: hireForm.startDate,
        ...(hireForm.salaryAmount ? { salaryAmount: hireForm.salaryAmount } : {}),
      });
      success('✅ Candidate hired — employee record created');
      setShowHireFor(null);
      setHireForm({ applicationId: '', employeeNumber: '', startDate: '', salaryAmount: '' });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not hire candidate')); }
  };

  const handleScheduleInterview = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/recruitment/interviews', interviewForm);
      success('✅ Interview scheduled');
      setShowForm(false);
      if (selectedApplicationId === interviewForm.candidateApplicationId) {
        const { data } = await apiClient.get(`/recruitment/applications/${selectedApplicationId}/interviews`);
        setInterviews(data.data);
      }
      setInterviewForm({ candidateApplicationId: '', scheduledAt: '', location: '', meetingLink: '' });
    } catch (err) { showError(getErrorMessage(err, 'Could not schedule interview')); }
  };

  const handleInterviewStatus = async (interviewId, status) => {
    try {
      await apiClient.patch(`/recruitment/interviews/${interviewId}/status`, { status });
      success('✅ Interview updated');
      const { data } = await apiClient.get(`/recruitment/applications/${selectedApplicationId}/interviews`);
      setInterviews(data.data);
    } catch (err) { showError(getErrorMessage(err, 'Could not update interview')); }
  };

  const handleCreateOffer = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/recruitment/offers', offerForm);
      success('✅ Offer created');
      setShowForm(false);
      setOfferForm({ candidateApplicationId: '', offeredSalary: '', proposedStartDate: '' });
    } catch (err) { showError(getErrorMessage(err, 'Could not create offer')); }
  };

  return (
    <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div className="page-header">
        <h1>Recruitment</h1>
        <p>Vacancies, candidates, and the hiring pipeline</p>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid #e5e7eb' }}>
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => { setTab(t); setShowForm(false); }}
            style={{
              padding: '0.75rem 1rem', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: 600, color: tab === t ? '#3b82f6' : '#6b7280',
              borderBottom: tab === t ? '2px solid #3b82f6' : '2px solid transparent',
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Vacancies' && (
        <section className="page-section">
          <div className="section-header">
            <h2>Vacancies</h2>
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
              <FiPlus size={14} /> {showForm ? 'Cancel' : 'New Vacancy'}
            </button>
          </div>
          {showForm && (
            <form onSubmit={handleAddVacancy} className="form-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="form-field"><label>Title *</label><input required value={vacancyForm.title} onChange={(e) => setVacancyForm(p => ({ ...p, title: e.target.value }))} /></div>
              <div className="form-field"><label>Location</label><input value={vacancyForm.location} onChange={(e) => setVacancyForm(p => ({ ...p, location: e.target.value }))} /></div>
              <div className="form-field full-width"><label>Description</label><input value={vacancyForm.description} onChange={(e) => setVacancyForm(p => ({ ...p, description: e.target.value }))} /></div>
              <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Create</button></div>
            </form>
          )}
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Title</th><th>Location</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {vacancies.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No vacancies yet</td></tr>
                ) : vacancies.map((v) => (
                  <tr key={v.id}>
                    <td style={{ fontWeight: 600 }}>{v.title}</td>
                    <td>{v.location || '—'}</td>
                    <td><span className="badge" style={{ background: v.status === 'open' ? '#10b981' : v.status === 'filled' ? '#3b82f6' : '#9ca3af', color: 'white' }}>{v.status}</span></td>
                    <td>
                      <div className="actions">
                        <select value={v.status} onChange={(e) => handleVacancyStatus(v.id, e.target.value)} style={{ padding: '0.3rem' }}>
                          <option value="open">Open</option>
                          <option value="on_hold">On Hold</option>
                          <option value="closed">Closed</option>
                          <option value="filled">Filled</option>
                        </select>
                        <button className="action-icon delete" title="Delete" onClick={() => handleDeleteVacancy(v.id)}>
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'Candidates' && (
        <section className="page-section">
          <div className="section-header">
            <h2>Candidates</h2>
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
              <FiPlus size={14} /> {showForm ? 'Cancel' : 'Add Candidate'}
            </button>
          </div>
          {showForm && (
            <form onSubmit={handleAddCandidate} className="form-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="form-field"><label>First Name *</label><input required value={candidateForm.firstName} onChange={(e) => setCandidateForm(p => ({ ...p, firstName: e.target.value }))} /></div>
              <div className="form-field"><label>Last Name *</label><input required value={candidateForm.lastName} onChange={(e) => setCandidateForm(p => ({ ...p, lastName: e.target.value }))} /></div>
              <div className="form-field"><label>Email</label><input type="email" value={candidateForm.email} onChange={(e) => setCandidateForm(p => ({ ...p, email: e.target.value }))} /></div>
              <div className="form-field"><label>Phone</label><input value={candidateForm.phone} onChange={(e) => setCandidateForm(p => ({ ...p, phone: e.target.value }))} /></div>
              <div className="form-field"><label>Source</label><input value={candidateForm.source} onChange={(e) => setCandidateForm(p => ({ ...p, source: e.target.value }))} placeholder="LinkedIn, referral..." /></div>
              <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Add</button></div>
            </form>
          )}
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Source</th><th>Actions</th></tr></thead>
              <tbody>
                {candidates.length === 0 ? (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No candidates yet</td></tr>
                ) : candidates.map((c) => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 600 }}>{c.firstName} {c.lastName}</td>
                    <td>{c.email || '—'}</td>
                    <td>{c.phone || '—'}</td>
                    <td>{c.source || '—'}</td>
                    <td>
                      <button className="action-icon delete" title="Delete" onClick={() => handleDeleteCandidate(c.id)}>
                        <FiTrash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'Applications' && (
        <section className="page-section">
          <div className="section-header">
            <h2>Applications</h2>
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
              <FiPlus size={14} /> {showForm ? 'Cancel' : 'New Application'}
            </button>
          </div>
          {showForm && (
            <form onSubmit={handleAddApplication} className="form-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="form-field">
                <label>Candidate *</label>
                <select required value={applicationForm.candidateId} onChange={(e) => setApplicationForm(p => ({ ...p, candidateId: e.target.value }))}>
                  <option value="">-- select --</option>
                  {candidates.map((c) => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label>Vacancy *</label>
                <select required value={applicationForm.jobVacancyId} onChange={(e) => setApplicationForm(p => ({ ...p, jobVacancyId: e.target.value }))}>
                  <option value="">-- select --</option>
                  {vacancies.map((v) => <option key={v.id} value={v.id}>{v.title}</option>)}
                </select>
              </div>
              <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Create</button></div>
            </form>
          )}
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Candidate</th><th>Vacancy</th><th>Stage</th><th>Actions</th></tr></thead>
              <tbody>
                {applications.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No applications yet</td></tr>
                ) : applications.map((a) => (
                  <tr key={a.id}>
                    <td style={{ fontWeight: 600 }}>{candidateName(a.candidateId)}</td>
                    <td>{vacancyTitle(a.jobVacancyId)}</td>
                    <td>
                      <select value={a.stage} onChange={(e) => handleStageChange(a.id, e.target.value)} style={{ padding: '0.3rem' }}>
                        <option value="applied">Applied</option>
                        <option value="screening">Screening</option>
                        <option value="interview">Interview</option>
                        <option value="offer">Offer</option>
                        <option value="hired">Hired</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </td>
                    <td>
                      <div className="actions">
                        {a.stage === 'offer' && a.status !== 'hired' && (
                          showHireFor === a.id ? (
                            <form onSubmit={handleHire} style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                              <input required placeholder="Emp #" style={{ width: '80px', padding: '0.3rem' }} value={hireForm.employeeNumber} onChange={(e) => setHireForm(p => ({ ...p, employeeNumber: e.target.value }))} />
                              <input required type="date" style={{ padding: '0.3rem' }} value={hireForm.startDate} onChange={(e) => setHireForm(p => ({ ...p, startDate: e.target.value }))} />
                              <input type="number" placeholder="Salary" style={{ width: '90px', padding: '0.3rem' }} value={hireForm.salaryAmount} onChange={(e) => setHireForm(p => ({ ...p, salaryAmount: e.target.value }))} />
                              <button type="submit" className="btn btn-sm btn-primary" style={{ width: 'auto' }}>Confirm</button>
                            </form>
                          ) : (
                            <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => setShowHireFor(a.id)}>
                              <FiUserCheck size={14} /> Hire
                            </button>
                          )
                        )}
                        <button className="action-icon delete" title="Delete" onClick={() => handleDeleteApplication(a.id)}>
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'Interviews' && (
        <section className="page-section">
          <div className="section-header">
            <h2>Interviews</h2>
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
              <FiPlus size={14} /> {showForm ? 'Cancel' : 'Schedule Interview'}
            </button>
          </div>
          {showForm && (
            <form onSubmit={handleScheduleInterview} className="form-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="form-field">
                <label>Application *</label>
                <select required value={interviewForm.candidateApplicationId} onChange={(e) => setInterviewForm(p => ({ ...p, candidateApplicationId: e.target.value }))}>
                  <option value="">-- select --</option>
                  {applications.map((a) => <option key={a.id} value={a.id}>{candidateName(a.candidateId)} — {vacancyTitle(a.jobVacancyId)}</option>)}
                </select>
              </div>
              <div className="form-field"><label>Scheduled At *</label><input required type="datetime-local" value={interviewForm.scheduledAt} onChange={(e) => setInterviewForm(p => ({ ...p, scheduledAt: e.target.value }))} /></div>
              <div className="form-field"><label>Location</label><input value={interviewForm.location} onChange={(e) => setInterviewForm(p => ({ ...p, location: e.target.value }))} /></div>
              <div className="form-field"><label>Meeting Link</label><input value={interviewForm.meetingLink} onChange={(e) => setInterviewForm(p => ({ ...p, meetingLink: e.target.value }))} /></div>
              <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Schedule</button></div>
            </form>
          )}
          <div className="form-field">
            <label>View interviews for application</label>
            <select value={selectedApplicationId} onChange={(e) => setSelectedApplicationId(e.target.value)}>
              <option value="">-- select an application --</option>
              {applications.map((a) => <option key={a.id} value={a.id}>{candidateName(a.candidateId)} — {vacancyTitle(a.jobVacancyId)}</option>)}
            </select>
          </div>
          {selectedApplicationId && (
            <div className="table-container">
              <table className="data-table">
                <thead><tr><th>When</th><th>Location</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {interviews.length === 0 ? (
                    <tr><td colSpan={4} style={{ textAlign: 'center', padding: '1.5rem', color: '#6b7280' }}>No interviews for this application</td></tr>
                  ) : interviews.map((i) => (
                    <tr key={i.id}>
                      <td>{new Date(i.scheduledAt).toLocaleString()}</td>
                      <td>{i.location || i.meetingLink || '—'}</td>
                      <td><span className="badge" style={{ background: i.status === 'completed' ? '#10b981' : i.status === 'cancelled' || i.status === 'no_show' ? '#ef4444' : '#f59e0b', color: 'white' }}>{i.status}</span></td>
                      <td>
                        {i.status === 'scheduled' && (
                          <div className="actions">
                            <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleInterviewStatus(i.id, 'completed')}>Complete</button>
                            <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleInterviewStatus(i.id, 'cancelled')}>Cancel</button>
                            <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleInterviewStatus(i.id, 'no_show')}>No-show</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {tab === 'Offers' && (
        <section className="page-section">
          <div className="section-header"><h2>Create Offer</h2></div>
          <form onSubmit={handleCreateOffer} className="form-grid">
            <div className="form-field">
              <label>Application *</label>
              <select required value={offerForm.candidateApplicationId} onChange={(e) => setOfferForm(p => ({ ...p, candidateApplicationId: e.target.value }))}>
                <option value="">-- select --</option>
                {applications.map((a) => <option key={a.id} value={a.id}>{candidateName(a.candidateId)} — {vacancyTitle(a.jobVacancyId)}</option>)}
              </select>
            </div>
            <div className="form-field"><label>Offered Salary (£)</label><input type="number" value={offerForm.offeredSalary} onChange={(e) => setOfferForm(p => ({ ...p, offeredSalary: e.target.value }))} /></div>
            <div className="form-field"><label>Proposed Start Date</label><input type="date" value={offerForm.proposedStartDate} onChange={(e) => setOfferForm(p => ({ ...p, proposedStartDate: e.target.value }))} /></div>
            <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Create Offer</button></div>
          </form>
          <p style={{ color: '#6b7280', fontSize: '0.85rem', marginTop: '1rem' }}>
            Once an offer's status is updated (accepted/declined), that happens on the candidate's side via a link — there's no UI here to record the response yet since it's meant to be candidate-facing. Say the word if you want an internal "record response" action added.
          </p>
        </section>
      )}
    </motion.div>
  );
};

export default Recruitment;